import { html, svg } from "uhtml";
import { signal } from "alien-signals";
import { Customised } from "../custom-element.js";
import { locales } from "../../models/locales.js";
import { locale } from "../../view-models/app-signals.js";
import { trackReaderEvent } from "../../view-models/page-signals.js";
import { readerEvents } from "../../constants.js";

// Safari only gained the unprefixed Fullscreen API in 16.4; iPadOS before
// that, and some embedded WebViews, have only the webkit-prefixed one.
// iPhone Safari has neither, so the button is not shown there.
export function fullscreenSupported(doc = document) {
  return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled);
}

function fullscreenElement() {
  return document.fullscreenElement || document.webkitFullscreenElement;
}

export const isFullscreen = signal(Boolean(fullscreenElement()));

// Recorded when the change happens rather than when it is asked for: the
// prefixed API reports no refusal, and leaving with Escape or a system
// gesture is a change too.
function updateFullscreen() {
  const active = Boolean(fullscreenElement());
  if (active === isFullscreen()) return;
  isFullscreen(active);
  trackReaderEvent(
    active ? readerEvents.EnterFullscreen : readerEvents.ExitFullscreen,
  );
}
document.addEventListener("fullscreenchange", updateFullscreen);
document.addEventListener("webkitfullscreenchange", updateFullscreen);

/** Enters full screen, or leaves it. */
export async function toggleFullscreen() {
  const root = document.documentElement;
  try {
    if (fullscreenElement()) {
      await (document.exitFullscreen || document.webkitExitFullscreen).call(
        document,
      );
    } else {
      await (root.requestFullscreen || root.webkitRequestFullscreen).call(root);
    }
  } catch (err) {
    // Refused, for example by a frame without allow="fullscreen".
    console.error(err);
  }
}

// The EPUB reader's expand and minimise icons (src/UI/Icons.ts).
const icons = {
  expand: svg`<path d="M1.166 7.286a.306.306 0 0 0 .306-.306V2.003L6.97 7.501a.308.308 0 0 0 .432 0 .305.305 0 0 0 0-.433L1.903 1.571h4.978a.306.306 0 0 0 0-.611h-5.53a.444.444 0 0 0-.105.012.306.306 0 0 0-.297.077.306.306 0 0 0-.077.297.52.52 0 0 0-.012.105v5.53c0 .168.137.305.306.305z"/><path d="M18.051 1.049a.307.307 0 0 0-.297-.078.504.504 0 0 0-.105-.012H12.12a.307.307 0 0 0 0 .612h4.978L11.6 7.069a.306.306 0 0 0 .433.432l5.498-5.498V6.98a.306.306 0 1 0 .613 0V1.451a.458.458 0 0 0-.014-.105.311.311 0 0 0-.079-.297z"/><path d="M.949 18.15a.3.3 0 0 0 .296.078.603.603 0 0 0 .105.012h5.53a.306.306 0 0 0 0-.613H1.903l5.498-5.498a.306.306 0 0 0-.432-.433l-5.498 5.498v-4.978a.307.307 0 0 0-.612 0v5.53c0 .036.004.07.012.105a.31.31 0 0 0 .078.299z"/><path d="M17.834 11.914a.306.306 0 0 0-.305.306v4.978L12.031 11.7a.306.306 0 0 0-.432.433l5.496 5.498h-4.978a.307.307 0 0 0 0 .613h5.53c.035 0 .07-.006.104-.012a.37.37 0 0 0 .079.012.304.304 0 0 0 .294-.387.442.442 0 0 0 .013-.104v-5.53a.302.302 0 0 0-.303-.309z"/>`,
  minimize: svg`<path d="M6.447 6.979H1.47a.307.307 0 0 0 0 .612H7a.457.457 0 0 0 .105-.012.308.308 0 0 0 .296-.077.304.304 0 0 0 .077-.296.473.473 0 0 0 .013-.106V1.57a.307.307 0 0 0-.612 0v4.977L1.382 1.049a.307.307 0 0 0-.433 0 .307.307 0 0 0 0 .433l5.498 5.497z"/><path d="M18.051 1.049a.307.307 0 0 0-.433 0l-5.497 5.498V1.57a.307.307 0 0 0-.613 0V7.1c0 .036.005.071.013.105a.303.303 0 0 0 .293.385.31.31 0 0 0 .08-.012.502.502 0 0 0 .104.012h5.528a.306.306 0 1 0 0-.612h-4.977l5.498-5.498a.301.301 0 0 0 .004-.431z"/><path d="M1.166 18.24a.299.299 0 0 0 .216-.09l5.498-5.498v4.978a.306.306 0 1 0 .612 0V12.1a.455.455 0 0 0-.012-.104.303.303 0 0 0-.373-.374.52.52 0 0 0-.105-.012H1.47a.307.307 0 0 0 0 .612h4.978L.949 17.72a.307.307 0 0 0 0 .433.31.31 0 0 0 .217.087z"/><path d="M11.51 17.63a.306.306 0 0 0 .613 0v-4.978l5.498 5.498a.304.304 0 0 0 .432 0 .307.307 0 0 0 0-.433l-5.497-5.499h4.977a.306.306 0 1 0 0-.612H12a.463.463 0 0 0-.105.012.303.303 0 0 0-.373.374.455.455 0 0 0-.012.104v5.534z"/>`,
};

export class CassisFullscreen extends Customised(HTMLElement) {
  static tagName = "cassis-fullscreen";
  signals() {
    return { locale: locale(), active: isFullscreen() };
  }
  render({ locale, active }) {
    const strings = active ? locales.minimizeIcon : locales.expandIcon;
    const label = strings[locale] || strings.en;
    return html`<button
      id="fullscreen-control"
      class="fullscreen"
      type="button"
      aria-label="${label}"
      title="${label}"
      @click=${toggleFullscreen}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 19 19"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-hidden="true"
        class="icon"
      >
        ${active ? icons.minimize : icons.expand}
      </svg>
      <span class="setting-text" id="fullscreen-label"
        >${locales.Fullscreen[locale] || locales.Fullscreen.en}</span
      >
    </button>`;
  }
}
CassisFullscreen.define();
